import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import klydoApiClient, {
  KlydoType,
  KlydoUpdate,
  PopulatedKlydo,
} from '../../../api/klydo';
import useKlydoMutation, {
  UseKlydoMutationOptions,
} from '../../../hooks/useKlydoMutation';

// Apply a partial KlydoUpdate to a cached klydo for the optimistic update.
//
// A shallow spread is enough because `KlydoUpdate` cannot carry `public` at all —
// the api strips that facet from this route. So there is no nested axis to merge,
// and the cached `public` is left exactly as the server last reported it.
const applyKlydoUpdate = <T extends PopulatedKlydo>(
  oldKlydo: T,
  update: KlydoUpdate,
): T => ({ ...oldKlydo, ...update });

const useUpsertDraftKlydoMutation = (
  options?: UseKlydoMutationOptions<
    void,
    { klydoId: string; klydo: KlydoUpdate },
    unknown
  >,
) => {
  const queryClient = useQueryClient();
  return useKlydoMutation({
    mutationKey: ['upsert-klydo'],
    mutationFn: ({ klydoId, klydo }) => {
      const cachedKlydo = queryClient.getQueryData<PopulatedKlydo>([
        'klydo',
        klydoId,
      ]);
      // A SEALED klydo is immutable through the draft upsert path — the api runs it
      // on the DraftKlydo model and rejects a sealed klydo outright. Keyed on the
      // seal, not on the review state: a klydo sealed by its collection has no
      // approved review of its own, and would otherwise slip past this.
      if (cachedKlydo?.type === KlydoType.Sealed) {
        return Promise.resolve();
      }
      return klydoApiClient.upsertDraftKlydo(klydoId, klydo);
    },
    onMutate: ({ klydoId, klydo }) => {
      const matchingQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: ['klydosList'] });

      queryClient.setQueryData(
        ['klydo', klydoId],
        (oldData: PopulatedKlydo | undefined) =>
          oldData ? applyKlydoUpdate(oldData, klydo) : oldData,
      );

      matchingQueries.forEach(({ queryKey }) => {
        queryClient.setQueryData<InfiniteData<PopulatedKlydo[]>>(
          queryKey,
          (oldData) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              pages: oldData.pages.map((page) =>
                page.map((oldklydo) =>
                  oldklydo.id === klydoId
                    ? applyKlydoUpdate(oldklydo, klydo)
                    : oldklydo,
                ),
              ),
            };
          },
        );
      });
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['klydosList'] });
      queryClient.invalidateQueries({ queryKey: ['klydo', variables.klydoId] });
    },
    ...options,
  });
};

export default useUpsertDraftKlydoMutation;
